<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :title="$i18n.t('sidebar.masterplanStreets.dialog.title')"
    :loading="loading"
    dialog-width="76%"
    max-height="90vh"
    header-shadow
    agree-text="dialog.agreeSave"
    @agree="save"
  >
    <template #body>
      <v-col cols="12" class="px-5">
        <v-row no-gutters align="center">
          <v-col cols="auto" class="pl-2">
            <base-rounded-button
              icon="mdi-plus"
              size="x-small"
              color="success"
              :tooltip="$i18n.t('sidebar.masterplanStreets.dialog.addRoadType')"
              @click="addItem"
            />
          </v-col>
        </v-row>
        <v-row v-if="items?.length" no-gutters class="pt-4 pb-1 text-body-2" align="center" justify="center">
          <v-col
            cols="auto"
            v-for="(col, idx) of mappedSituationsCols"
            :key="idx"
            :style="`width: ${col.width}`"
            class="font-weight-medium masterplan-street-calculator-text"
          >
            {{ col.translationPath ? $i18n.t(col.translationPath) : col.attributes?.at(-1)?.label || '' }}
          </v-col>
        </v-row>
        <v-row no-gutters style="position: relative">
          <template v-if="items?.length >= 2">
            <span class="side l-side font-weight-medium">L</span>
            <span class="side r-side font-weight-medium">R</span>
          </template>
          <draggable-list
            :value.sync="items"
            allow-elements-on-main-level
            disable-group-adding
            fill-width
            @change="onOrderUpdate"
          >
            <template #listElement="item">
              <masterplan-streets-widget-dialog-element
                :cols="mappedSituationsCols"
                :item.sync="item"
                :lighting-situation-values="lightingSituationValues"
                :type-attribute-dict="typeAttributeDict"
                :loading="loading"
                @refreshFeaturesMapping="refreshFeaturesMapping"
                @onCalculateToggle="onCalculateToggle"
                @onCopyToggle="onCopyToggle"
                @onDelete="onDelete"
              />
            </template>
          </draggable-list>
        </v-row>
        <v-row no-gutters class="text-body-2 pt-4">
          <v-col cols="7">
            <v-row no-gutters class="text-body-2 font-weight-medium masterplan-street-calculator-text pb-3">
              {{ $i18n.t('sidebar.masterplanStreets.dialog.lanternGeometry') }}
            </v-row>
            <v-row no-gutters>
              <v-col cols="3">
                <img :src="require('@/assets/images/mastdiagram.svg')" width="100%" style="max-width: 138px" />
              </v-col>
              <v-col cols="9" class="px-5">
                <v-row no-gutters v-for="(col, idx) of mappedStreetsCols.left" :key="idx" align="center" class="py-1">
                  <v-col cols="7" class="text-left text-body-2 font-weight-medium masterplan-street-calculator-text">
                    {{ col.label }}
                  </v-col>
                  <v-col cols="3">
                    <data-input
                      v-model="computedStreetProperties[col.name]"
                      v-bind="{
                        ...$_filterObjectKeys(col, { notAllowedKeys: ['label'] }),
                      }"
                    />
                  </v-col>
                  <v-col cols="1" class="text-left pl-1">
                    {{ col.unit }}
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="5">
            <v-row no-gutters class="text-body-2 font-weight-medium masterplan-street-calculator-text pb-3">
              {{ $i18n.t('sidebar.masterplanStreets.dialog.arrangement') }}
            </v-row>
            <v-row no-gutters v-for="(col, idx) of mappedStreetsCols.right" :key="idx" align="center" class="py-1">
              <template v-if="col.name === lightingStreetAttributes.arrangement_attribute">
                <v-col cols="auto" class="mr-2">
                  <v-row no-gutters class="font-weight-medium pb-2">
                    L
                  </v-row>
                  <v-row no-gutters class="font-weight-medium pt-2">
                    R
                  </v-row>
                </v-col>
                <v-col v-for="(arrangement, idx) of arrangementAttributeDictList" :key="idx" cols="auto" class="px-1">
                  <img
                    v-if="getArrangementImg(arrangement)"
                    :src="getArrangementImg(arrangement)"
                    width="60"
                    height="60"
                    :class="{
                      'masterplan-street-arrangement-disabled':
                        computedStreetProperties[col.name] !== arrangement.value,
                    }"
                    v-on="getArrangementListeners(arrangement)"
                  />
                </v-col>
              </template>
              <template v-else>
                <v-col cols="7" class="text-left text-body-2 font-weight-medium masterplan-street-calculator-text">
                  {{ col.label }}
                </v-col>
                <v-col cols="3">
                  <data-input
                    v-model="computedStreetProperties[col.name]"
                    v-bind="{
                      ...$_filterObjectKeys(col, { notAllowedKeys: ['label'] }),
                    }"
                  />
                </v-col>
                <v-col cols="1" class="text-left pl-1">
                  {{ col.unit }}
                </v-col>
              </template>
            </v-row>
          </v-col>
        </v-row>
        <main-large-dialog
          v-if="isCopyLoaded"
          :is-visible.sync="isCopyVisible"
          :title="$i18n.t('sidebar.masterplanStreets.dialog.copyFrom', { value: currentCopyData.text })"
          agree-text="dialog.agreeCopy"
          header-shadow
          dialog-width="30%"
          @agree="copy"
        >
          <template #body>
            <v-col cols="12" class="pt-3">
              <v-row
                no-gutters
                v-for="(element, idx) of currentCopyData.attributesLabels"
                :key="`label-${idx}`"
                class="text-body-2 pb-1"
              >
                <v-col cols="5" class="text-left font- font-weight-medium"> {{ element.label }}: </v-col>
                <v-col cols="auto" class="text-left font- font-weight-medium">
                  {{ element.value }}
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-checkbox
                  :value="currentCopyData.otherItems?.find(element => element.checked)"
                  :on-icon="
                    currentCopyData.otherItems?.every(element => element.checked)
                      ? 'mdi-checkbox-marked'
                      : 'mdi-minus-box'
                  "
                  @change="toggleAllOtherCopyItems"
                  hide-details
                >
                  <template #label>
                    <div class="text-body-2 text-left" style="color: #202124">
                      {{ $i18n.t('dialog.selectAll') }}
                    </div>
                  </template>
                </v-checkbox>
              </v-row>
              <v-row no-gutters v-for="otherItem of currentCopyData.otherItems" :key="otherItem.value">
                <v-checkbox v-model="otherItem.checked" hide-details>
                  <template #label>
                    <div class="text-body-2 text-left" style="color: #202124">
                      {{ otherItem.text }}
                    </div>
                  </template>
                </v-checkbox>
              </v-row>
            </v-col>
          </template>
        </main-large-dialog>
        <main-large-dialog
          v-if="isCalculatorLoaded"
          :is-visible.sync="isCalculatorVisible"
          :is-valid="calculatorValid"
          :title="$i18n.t('sidebar.masterplanStreets.dialog.calculate', { value: currentCalculatorData.text })"
          agree-text="button.calculate"
          header-shadow
          dialog-width="25%"
          min-body-height="200px"
          @agree="calculate"
        >
          <template #body>
            <v-col cols="12" class="pt-3">
              <v-form v-model="calculatorValid" ref="calculator-form">
                <v-row no-gutters v-for="(attribute, idx) of currentCalculatorData.calculatorMetadata" :key="idx">
                  <v-col cols="12">
                    <data-input
                      v-model="
                        currentCalculatorData.calculator[currentCalculatorData.situation].params[attribute.label]
                      "
                      v-bind="attribute"
                    />
                  </v-col>
                </v-row>
              </v-form>
            </v-col>
          </template>
        </main-large-dialog>
      </v-col>
    </template>
  </main-large-dialog>
</template>
<script>
import { call, get } from 'vuex-pathify';
import { sum } from 'lodash';

import DataInput from '@/components/DataInput';
import DraggableList from '@/components/DraggableList';
import MainLargeDialog from '@/components/MainLargeDialog';
import MasterplanStreetsWidgetDialogElement from '@/components/MasterplanStreetsWidgetDialogElement';

export default {
  name: 'MasterplanStreetsWidgetDialog',
  components: { DataInput, DraggableList, MainLargeDialog, MasterplanStreetsWidgetDialogElement },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    fid: {
      type: Number,
      required: true,
    },
    lid: {
      type: Number,
      required: true,
    },
    features: {
      type: Array,
      default: () => {
        return [];
      },
    },
    featuresMappingFunction: {
      type: Function,
    },
    arrangementAttributeDict: {
      type: Object,
      default: () => {
        return {};
      },
    },
    typeAttributeDict: {
      type: Object,
      default: () => {
        return {};
      },
    },
    streetProperties: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  computed: {
    token: get('authentication/token'),
    dataSources: get('layers/metadata'),
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },

    calculatorMetadata: get('admin/settingsValues@masterplan_2_streets_situations_data'),
    calculatorMetadataSituations() {
      return Object.keys(this.calculatorMetadata);
    },
    masterplanSituationsDataSourceMapping: get(
      'admin/modulesMapping@masterplan_2_streets@masterplan_situation_datasource'
    ),
    lightingSituationDataSourceName() {
      return this.masterplanSituationsDataSourceMapping?.datasource || '';
    },
    lightingSituationDataSource() {
      return this.dataSources[this.lightingSituationDataSourceName] || undefined;
    },
    lightingSituationValues() {
      return Object.keys(this.masterplanSituationsDataSourceMapping?.settings)
        .filter(key => key.includes('_value'))
        .reduce((total, current) => {
          total[current] = this.masterplanSituationsDataSourceMapping?.settings[current];
          return total;
        }, {});
    },
    lightingSituationAttributes() {
      return Object.keys(this.masterplanSituationsDataSourceMapping?.settings)
        .filter(key => key.includes('_attribute'))
        .reduce((total, current) => {
          total[current] = this.masterplanSituationsDataSourceMapping?.settings[current];
          return total;
        }, {});
    },
    lightingSituationAttributesMetadata() {
      return Object.entries(this.lightingSituationAttributes).reduce((total, [key, value]) => {
        const attribute = this.lightingSituationDataSource?.attributes_schema.attributes.find(
          attribute => attribute.name === value
        );
        const unit = attribute.measurement_unit?.symbol;
        total[key] = {
          dataType: { ...attribute.data_type, ...(attribute.allowed_values ? { name: 'select' } : {}) },
          ...(attribute.allowed_values ? { items: attribute.allowed_values } : {}),
          name: value,
          label: `${attribute.verbose_name}${unit ? ` [${unit}]` : ''}`,
          hideDetails: true,
          persistentPlaceholder: true,
        };
        return total;
      }, {});
    },
    mappedSituationsCols() {
      return this.situationsCols.map(col => {
        const { disabledTypes, enabledTypes } = col;
        return {
          ...col,
          attributes:
            col.attributes?.map(attribute => {
              return {
                ...attribute,
                ...this.lightingSituationAttributesMetadata[`${attribute.name}_attribute`],
                readonly: this.loading,
              };
            }) || [],
          ...(disabledTypes || enabledTypes
            ? { disabledFunction: this.getDisabledFunction(disabledTypes, enabledTypes) }
            : {}),
        };
      });
    },
    situationsCols() {
      return [
        {
          name: 'type',
          width: '8%',
          attributes: [{ name: 'type' }],
        },
        {
          name: 'situation',
          width: '16%',
          attributes: [{ name: 'situation' }, { name: 'class' }],
          disabledTypes: ['green_strip', 'median_strip'],
        },
        {
          name: 'calculator',
          width: '15%',
          translationPath: 'sidebar.masterplanStreets.dialog.calculator',
          disabledTypes: ['green_strip', 'median_strip'],
        },
        {
          name: 'options1',
          width: '5%',
          copy: ['situation', 'calculator'],
        },
        {
          name: 'width',
          width: '8%',
          attributes: [{ name: 'width' }],
        },
        {
          name: 'lanes',
          width: '8%',
          attributes: [{ name: 'lanes' }],
          enabledTypes: ['lane'],
        },
        {
          name: 'surface',
          width: '12%',
          attributes: [{ name: 'surface' }],
          disabledTypes: ['green_strip', 'median_strip'],
        },
        {
          name: 'reflectivity',
          width: '13%',
          attributes: [{ name: 'reflectivity' }],
          disabledTypes: ['green_strip', 'median_strip'],
        },
        {
          name: 'brightness',
          width: '8%',
          attributes: [{ name: 'brightness' }],
          disabledTypes: ['green_strip', 'median_strip'],
        },
        {
          name: 'options2',
          width: '7%',
          copy: ['width', 'lanes', 'surface', 'reflectivity', 'brightness'],
        },
      ];
    },

    computedStreetProperties: {
      get() {
        return this.streetProperties;
      },
      set(nV) {
        this.$emit('update:streetProperties', nV);
      },
    },
    masterplanStreetsDataSourceMapping: get('admin/modulesMapping@masterplan_2_streets@masterplan_street_datasource'),
    lightingStreetDataSourceName() {
      return this.masterplanStreetsDataSourceMapping?.datasource || '';
    },
    lightingStreetDataSource() {
      return this.dataSources[this.lightingStreetDataSourceName] || undefined;
    },
    lightingStreetAttributes() {
      return Object.keys(this.masterplanStreetsDataSourceMapping?.settings)
        .filter(key => key.includes('_attribute'))
        .reduce((total, current) => {
          total[current] = this.masterplanStreetsDataSourceMapping?.settings[current];
          return total;
        }, {});
    },
    lightingStreetAttributesMetadata() {
      return Object.entries(this.lightingStreetAttributes).reduce((total, [key, value]) => {
        const attribute = this.lightingStreetDataSource?.attributes_schema.attributes.find(
          attribute => attribute.name === value
        );
        const unit = attribute.measurement_unit?.symbol;
        total[key] = {
          dataType: { ...attribute.data_type, ...(attribute.allowed_values ? { name: 'select' } : {}) },
          ...(attribute.allowed_values ? { items: attribute.allowed_values } : {}),
          name: value,
          unit,
          label: attribute.verbose_name,
          hideDetails: true,
          persistentPlaceholder: true,
        };
        return total;
      }, {});
    },
    mappedStreetsCols() {
      return Object.entries(this.streetCols).reduce((total, [key, cols]) => {
        total[key] = cols.map(col => {
          const metadata = this.lightingStreetAttributesMetadata[`${col.name}_attribute`];
          return {
            ...col,
            ...metadata,
            unit: metadata.unit || col.defaultUnit,
            label: col.symbol ? `${col.symbol} ${metadata.label}` : metadata.label,
            readonly: this.loading,
          };
        });
        return total;
      }, {});
    },
    streetCols() {
      return {
        left: [
          {
            name: 'mount_height',
            symbol: 'a',
            defaultUnit: 'm',
          },
          {
            name: 'overhang',
            symbol: 'b',
            defaultUnit: 'm',
          },
          {
            name: 'road_distance',
            symbol: 'c',
            defaultUnit: 'm',
          },
          {
            name: 'pick_arm_length',
            symbol: 'd',
            defaultUnit: 'm',
          },
          {
            name: 'tilt_angle',
            symbol: 'α',
            defaultUnit: '°',
          },
        ],
        right: [
          {
            name: 'arrangement',
          },
          {
            name: 'poles_distance',
            defaultUnit: 'm',
          },
        ],
      };
    },
    arrangementAttributeDictList() {
      return this.$_objectToArray(this.arrangementAttributeDict, 'value');
    },
  },
  data: () => ({
    deletedFeaturesIds: [],
    loading: false,
    items: [],

    isCopyLoaded: false,
    isCopyVisible: false,
    currentCopyData: {},

    calculatorValid: false,
    currentCalculatorData: {},
    isCalculatorLoaded: false,
    isCalculatorVisible: false,
  }),
  methods: {
    editMultipleDataSourceFeatures: call('layers/editMultipleDataSourceFeatures'),
    editFeature: call('layers/editDatasourceFeature'),

    addItem() {
      this.items = this.featuresMappingFunction([
        {
          properties: {
            [this.lightingSituationAttributes.order_attribute]: 0,
            [this.lightingSituationAttributes.street_id_attribute]: this.fid,
          },
        },
        ...this.items.map(item => {
          return {
            ...item,
            properties: {
              ...item.properties,
              [this.lightingSituationAttributes.order_attribute]:
                item.properties[this.lightingSituationAttributes.order_attribute] + 1,
            },
          };
        }),
      ]);
    },
    onCopyToggle({ item, attributesValues, attributesLabels }) {
      this.currentCopyData = {
        text: item.text,
        attributesValues,
        attributesLabels,
        otherItems: this.items
          .filter(item => {
            return Object.values(attributesValues).find(element => {
              const disabledTypes = this.situationsCols.find(c => c.name === element.col)?.disabledTypes;
              return (
                !disabledTypes?.length ||
                !disabledTypes.find(
                  type =>
                    this.lightingSituationValues[`type_${type}_value`] ===
                    item.properties[this.lightingSituationAttributes['type_attribute']]
                )
              );
            });
          })
          .map((i, idx) => {
            return {
              value: idx,
              text: i.text,
              checked: false,
              type: i.properties[this.lightingSituationAttributes['type_attribute']],
            };
          })
          .filter((i, idx) => idx !== item.idx),
      };
      this.isCopyLoaded = true;
      this.isCopyVisible = true;
    },
    toggleAllOtherCopyItems(e) {
      this.currentCopyData.otherItems = this.currentCopyData.otherItems.map(item => {
        return { ...item, checked: e };
      });
    },
    copy() {
      const calculationAttribute = this.lightingSituationAttributes['calculation_attribute'];
      const withCalculator = Object.keys(this.currentCopyData.attributesValues).includes(calculationAttribute);
      this.currentCopyData.otherItems.forEach(item => {
        if (item.checked) {
          const attributesValues = Object.entries(this.currentCopyData.attributesValues).reduce((total, [key, ent]) => {
            const { col, value } = ent;
            const colData = this.mappedSituationsCols.find(c => c.name === col);
            if (!colData.disabledFunction || !colData.disabledFunction(item.type)) {
              total[key] = value;
            }
            return total;
          }, {});
          const oldProps = JSON.parse(JSON.stringify(this.items[item.value].properties));
          if (withCalculator) {
            const oldPropsCalculation = JSON.parse(oldProps[calculationAttribute] || null) || this.getEmptyCalculator();
            const newPropsCalculation =
              JSON.parse(attributesValues[calculationAttribute] || null) || this.getEmptyCalculator();
            const newPropsSituation = attributesValues[this.lightingSituationAttributes['situation_attribute']];

            const newPropsCalculationFixed = newPropsSituation
              ? {
                  ...oldPropsCalculation,
                  [newPropsSituation]: newPropsCalculation[newPropsSituation] || { class: null, params: {} },
                }
              : {
                  ...oldPropsCalculation,
                  [newPropsSituation]: { class: null, params: {} },
                };

            this.$set(this.items[item.value], 'properties', {
              ...oldProps,
              ...this.$_filterObjectKeys(attributesValues, {
                notAllowedKeys: [calculationAttribute],
              }),
              [calculationAttribute]: JSON.stringify(newPropsCalculationFixed),
            });
          } else {
            this.$set(this.items[item.value], 'properties', {
              ...oldProps,
              ...attributesValues,
            });
          }
        }
      });
      this.items = this.featuresMappingFunction(this.items);
      this.isCopyVisible = false;
      this.currentCopyData = {};
    },

    onCalculateToggle(item) {
      const calculatorValue = item.properties[this.lightingSituationAttributes.calculation_attribute];
      const situationValue = item.properties[this.lightingSituationAttributes.situation_attribute];
      if (!calculatorValue) {
        this.setEmptyCalculator(item);
      } else {
        try {
          this.currentCalculatorData = {
            idx: item.idx,
            text: item.text,
            situation: situationValue,
            calculator: JSON.parse(item.properties[this.lightingSituationAttributes.calculation_attribute]),
            calculatorMetadata: this.getCurrentCalculatorMetadata(situationValue),
          };
        } catch {
          this.setEmptyCalculator(item);
        }
      }
      this.isCalculatorLoaded = true;
      this.isCalculatorVisible = true;
      this.$refs['calculator-form']?.resetValidation();
    },
    setEmptyCalculator(item) {
      const situationValue = item.properties[this.lightingSituationAttributes.situation_attribute];
      this.currentCalculatorData = {
        ...{ idx: item.idx, text: item.text, situation: situationValue },
        calculator: this.getEmptyCalculator(),
        calculatorMetadata: this.getCurrentCalculatorMetadata(
          item.properties[this.lightingSituationAttributes.situation_attribute]
        ),
      };
    },
    getEmptyCalculator() {
      return {
        ...this.calculatorMetadataSituations.reduce((total, current) => {
          total[current] = {
            class: null,
            params: {},
          };
          return total;
        }, {}),
      };
    },
    getCurrentCalculatorMetadata(value) {
      return Object.entries(this.calculatorMetadata[value]).map(([key, value]) => {
        const items = Object.entries(value).map(([k, v]) => {
          return { text: k, value: v };
        });
        return {
          label: key,
          dataType: { name: 'select' },
          items,
          persistentPlaceholder: true,
          rules: [v => !!v || v === 0 || 'rules.required'],
        };
      });
    },
    calculate() {
      const situationValue = this.currentCalculatorData.situation;
      const sumValue = sum(Object.values(this.currentCalculatorData.calculator[situationValue].params));
      let classValue = 6 - sumValue;
      if (situationValue === 'C' && classValue > 5) classValue = 5;
      else if (classValue > 6) classValue = 6;
      else if (classValue < 0) classValue = 0;

      const calculator = JSON.stringify({
        ...this.currentCalculatorData.calculator,
        [situationValue]: { ...this.currentCalculatorData.calculator[situationValue], class: classValue },
      });
      this.$set(
        this.items[this.currentCalculatorData.idx].properties,
        this.lightingSituationAttributes.calculation_attribute,
        calculator
      );
      this.items = this.featuresMappingFunction(this.items);
      this.isCalculatorVisible = false;
      this.currentCalculatorData = {};
    },

    onDelete(item) {
      this.items.splice(item.idx, 1);
      this.items = this.featuresMappingFunction(
        this.items.map((i, idx) => {
          return { ...i, properties: { ...i.properties, [this.lightingSituationAttributes.order_attribute]: idx } };
        })
      );
      if (item.id || item.id === 0) this.deletedFeaturesIds.push(item.id);
    },

    onOrderUpdate(e) {
      if (e?.moved) {
        this.items.forEach((item, idx) => {
          this.$set(item.properties, this.lightingSituationAttributes.order_attribute, idx);
        });
        this.items = this.featuresMappingFunction(this.items);
      }
    },
    refreshFeaturesMapping() {
      this.items = this.featuresMappingFunction(this.items);
    },
    getDisabledFunction(disabledTypes = [], enabledTypes = []) {
      const mappedDisabledTypes = disabledTypes?.map(type => this.lightingSituationValues[`type_${type}_value`]);
      const mappedEnabledTypes = enabledTypes?.map(type => this.lightingSituationValues[`type_${type}_value`]);
      return type => {
        if (mappedDisabledTypes?.length) return mappedDisabledTypes.includes(type);
        if (mappedEnabledTypes) return !mappedEnabledTypes.includes(type);
        return false;
      };
    },
    getArrangementListeners(arrangement) {
      return {
        ...(this.loading
          ? {}
          : {
              click: () => {
                this.setArrangement(arrangement.value);
              },
            }),
      };
    },
    getArrangementImg(item) {
      return item?.image ? `${process.env.VUE_APP_API_URL}/dicts_images/${item.image}?token=${this.token}` : '';
    },
    setArrangement(value) {
      this.$set(this.computedStreetProperties, this.lightingStreetAttributes.arrangement_attribute, value);
    },

    async save() {
      this.loading = true;
      try {
        const addedItems = this.items.filter(item => !item.id && item.id !== 0);
        const editedItems = this.items.filter(item => item.id || item.id === 0);
        const payload = {
          data_source_name: this.lightingSituationDataSourceName,
          delete: {
            features_ids: this.deletedFeaturesIds,
          },
          insert: addedItems.map(item => {
            return { id: null, ...item.properties };
          }),
          update: editedItems.map(item => {
            return { fid: item.id, properties: item.properties };
          }),
        };
        await Promise.all([
          this.editMultipleDataSourceFeatures(payload),
          this.editFeature({
            datasource: this.lightingStreetDataSourceName,
            id: this.fid,
            body: this.streetProperties,
            params: { layer_id: this.lid },
          }),
        ]);
        this.computedIsVisible = false;
        this.$emit('onSave');
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    computedIsVisible: {
      handler(nV) {
        if (nV) {
          this.items = JSON.parse(JSON.stringify(this.features));
          this.deletedFeaturesIds = [];
          this.currentCopyData = {};
          this.currentCalculatorData = {};
        }
      },
      immediate: true,
    },
  },
};
</script>
<style lang="scss" scoped>
.masterplan-street-calculator-text {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}
.side {
  position: absolute;
  left: -20px;
}
.l-side {
  top: 20px;
}
.r-side {
  bottom: 20px;
}
.masterplan-street-arrangement-disabled {
  opacity: 0.5;
  &:hover {
    opacity: 1;
    cursor: pointer;
  }
}
</style>
